import type {ReactElement} from 'react';
import {useEffect, useId, useRef, useState} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {Pill} from '@Components/pill/pill';
import {VisuallyHiddenText} from '@Components/visually-hidden-text/visually-hidden-text';
import {PillSize, PillType} from '@Components/pill/pill.types';
import {useAppSelector} from '@/hooks';
import {useAiChatMessage} from '@Components/ai-chat-window/providers/ai-chat-message.context';
import {GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {assertNever} from '@Utils/assert-never.util';
import {ProposedChangeRow} from './components/proposed-change-row/proposed-change-row';
import {ProposedChangeNotice} from './components/proposed-change-notice/proposed-change-notice';
import {ProposedChangeNoticeTone} from './components/proposed-change-notice/proposed-change-notice.types';
import {ProposedChangeFocusTarget, ProposedChangeStatus, ProposedChangeTapOutcome, UNDO_COMPONENT_ACTION} from './proposed-change-card.types';
import type {ProposedChangeActionRequest, ProposedChangeCardProps, ProposedChangeTapResult} from './proposed-change-card.types';
import {sendProposedChangeAction} from './proposed-change-card.library';
import styles from './proposed-change-card.module.scss';

/**
 * A server-built "label: before → after" change the user applies with one tap and can undo. Every string arrives translated;
 * what the change does is the server's `actionKey` handler, so any tool can offer one. No growl on apply: the applied pill
 * replaces the button in place and the chat's polite log announces it. Every answer from the server sets the card to the
 * status the server stored, and the edit link and button open the absolute `editUrl` in a new tab so the chat stays open.
 */
export function ProposedChangeCard({title, appliedTitle, subtitle, rows, status: offeredStatus, labels, editUrl, actionKey}: ProposedChangeCardProps): ReactElement {
  const {chatId, messageId} = useAiChatMessage();
  const hasWriteAccess = useAppSelector((state) => state.aiChatWindow[chatId]?.hasWriteAccess) === true;
  const headingId = useId();
  const editLinkId = useId();
  const newTabHintId = useId();
  const [status, setStatus] = useState<ProposedChangeStatus>(offeredStatus);
  const [isSending, setIsSending] = useState(false);
  const [hasUndoFailed, setHasUndoFailed] = useState(false);
  const applyButtonRef = useRef<HTMLButtonElement>(null);
  const undoButtonRef = useRef<HTMLButtonElement>(null);
  const editButtonRef = useRef<HTMLAnchorElement>(null);
  const pendingFocusRef = useRef<ProposedChangeFocusTarget | null>(null);

  const getFocusTargetElement = (focusTarget: ProposedChangeFocusTarget): HTMLElement | null => {
    switch (focusTarget) {
      case ProposedChangeFocusTarget.APPLY:
        return applyButtonRef.current;
      case ProposedChangeFocusTarget.UNDO:
        return undoButtonRef.current;
      case ProposedChangeFocusTarget.EDIT:
        return editButtonRef.current;
      default:
        return assertNever(focusTarget);
    }
  };

  useEffect(() => {
    const focusTarget = pendingFocusRef.current;
    if (focusTarget === null) {
      return;
    }

    pendingFocusRef.current = null;
    getFocusTargetElement(focusTarget)?.focus();
  });

  const getFocusTargetAfterSettling = (settledStatus: ProposedChangeStatus): ProposedChangeFocusTarget => {
    switch (settledStatus) {
      case ProposedChangeStatus.APPLIED:
        return ProposedChangeFocusTarget.UNDO;
      case ProposedChangeStatus.STALE:
        return ProposedChangeFocusTarget.EDIT;
      case ProposedChangeStatus.PENDING:
      case ProposedChangeStatus.FAILED:
        return ProposedChangeFocusTarget.APPLY;
      default:
        return assertNever(settledStatus);
    }
  };

  const settleOn = (settledStatus: ProposedChangeStatus): void => {
    setStatus(settledStatus);
    pendingFocusRef.current = getFocusTargetAfterSettling(settledStatus);
  };

  const sendTap = async (request: ProposedChangeActionRequest): Promise<ProposedChangeTapResult> => {
    setIsSending(true);
    setHasUndoFailed(false);
    const result = await sendProposedChangeAction(request);
    setIsSending(false);
    return result;
  };

  const applyChange = async (): Promise<void> => {
    const result = await sendTap({chatId, messageId});
    switch (result.outcome) {
      case ProposedChangeTapOutcome.SETTLED:
        settleOn(result.status);
        if (result.status === ProposedChangeStatus.APPLIED) {
          trackGA4CustomEvent(GA4EventName.AICHAT_PROPOSED_CHANGE_APPLIED, {[GA4EventParamName.ACTION]: actionKey});
        }
        return;
      case ProposedChangeTapOutcome.STALE:
        settleOn(ProposedChangeStatus.STALE);
        return;
      case ProposedChangeTapOutcome.FAILED:
        settleOn(ProposedChangeStatus.FAILED);
        return;
      default:
        assertNever(result);
    }
  };

  /** A failed undo leaves the change saved, so the card stays applied and only says the undo did not go through. */
  const undoChange = async (): Promise<void> => {
    const result = await sendTap({chatId, messageId, undo: UNDO_COMPONENT_ACTION});
    switch (result.outcome) {
      case ProposedChangeTapOutcome.SETTLED:
        settleOn(result.status);
        if (result.status === ProposedChangeStatus.PENDING) {
          trackGA4CustomEvent(GA4EventName.AICHAT_PROPOSED_CHANGE_UNDONE, {[GA4EventParamName.ACTION]: actionKey});
        }
        return;
      case ProposedChangeTapOutcome.STALE:
        settleOn(ProposedChangeStatus.STALE);
        return;
      case ProposedChangeTapOutcome.FAILED:
        setHasUndoFailed(true);
        pendingFocusRef.current = ProposedChangeFocusTarget.UNDO;
        return;
      default:
        assertNever(result);
    }
  };

  const getHeading = (): string => {
    if (status === ProposedChangeStatus.APPLIED) {
      return appliedTitle;
    }

    return hasWriteAccess ? title : labels.readOnlyTitle;
  };

  const renderSubtitle = (): ReactElement | null => {
    if (subtitle === null) {
      return null;
    }

    return <Text val={subtitle} size={TextSize.SM} className={styles.proposedChangeCardSubtitle} />;
  };

  const renderNotice = (): ReactElement | null => {
    if (!hasWriteAccess) {
      return null;
    }

    if (hasUndoFailed) {
      return <ProposedChangeNotice tone={ProposedChangeNoticeTone.ERROR} text={labels.undoFailed} />;
    }

    switch (status) {
      case ProposedChangeStatus.STALE:
        return <ProposedChangeNotice tone={ProposedChangeNoticeTone.WARNING} text={labels.stale} />;
      case ProposedChangeStatus.FAILED:
        return <ProposedChangeNotice tone={ProposedChangeNoticeTone.ERROR} text={labels.failed} />;
      case ProposedChangeStatus.PENDING:
      case ProposedChangeStatus.APPLIED:
        return null;
      default:
        return assertNever(status);
    }
  };

  const renderApplyButton = (): ReactElement => (
    <Button
      ref={applyButtonRef}
      text={labels.apply}
      type={ButtonType.PRIMARY}
      size={ButtonSize.SMALL}
      isLoading={isSending}
      onClick={(): void => {
        void applyChange();
      }}
    />
  );

  const renderUndoButton = (): ReactElement | null => {
    if (!hasWriteAccess) {
      return null;
    }

    return (
      <Button
        ref={undoButtonRef}
        text={labels.undo}
        type={ButtonType.SECONDARY}
        size={ButtonSize.SMALL}
        icon="icon-undo"
        isLoading={isSending}
        onClick={(): void => {
          void undoChange();
        }}
      />
    );
  };

  const renderEditLink = (): ReactElement | null => {
    if (editUrl === null) {
      return null;
    }

    return (
      <>
        <Text
          val={labels.edit}
          href={editUrl}
          size={TextSize.SM}
          bold
          className={styles.proposedChangeEditLink}
          attributes={{id: editLinkId, 'aria-labelledby': `${editLinkId} ${newTabHintId}`, target: '_blank', rel: 'noopener noreferrer'}}
        />
        <VisuallyHiddenText id={newTabHintId} val={window.i18next.t('pmwjs_ai_opens_in_new_tab')} />
      </>
    );
  };

  const renderEditButton = (): ReactElement | null => {
    if (editUrl === null) {
      return null;
    }

    return (
      <Button
        ref={editButtonRef}
        href={editUrl}
        text={labels.edit}
        type={ButtonType.PRIMARY}
        size={ButtonSize.SMALL}
        attributes={{target: '_blank', rel: 'noopener noreferrer'}}
      >
        <VisuallyHiddenText val={window.i18next.t('pmwjs_ai_opens_in_new_tab')} />
      </Button>
    );
  };

  const renderActions = (): ReactElement | null => {
    switch (status) {
      case ProposedChangeStatus.APPLIED:
        return (
          <>
            <Pill label={labels.applied} type={PillType.SUCCESS} icon="icon-check" size={PillSize.SM} />
            {renderUndoButton()}
          </>
        );
      case ProposedChangeStatus.PENDING:
        return renderApplyButton();
      case ProposedChangeStatus.FAILED:
        return (
          <>
            {renderApplyButton()}
            {renderEditLink()}
          </>
        );
      case ProposedChangeStatus.STALE:
        return renderEditButton();
      default:
        return assertNever(status);
    }
  };

  const renderFooter = (): ReactElement | null => {
    const isOwnerOrApplied = hasWriteAccess || status === ProposedChangeStatus.APPLIED;
    const actions = isOwnerOrApplied ? renderActions() : null;
    if (actions === null) {
      return null;
    }

    return <footer className={styles.proposedChangeCardFooter}>{actions}</footer>;
  };

  return (
    <article className={styles.proposedChangeCard} aria-labelledby={headingId}>
      <header className={styles.proposedChangeCardHeader}>
        <Text val={getHeading()} size={TextSize.MD} bold className={styles.proposedChangeCardTitle} attributes={{id: headingId}} />
        {renderSubtitle()}
      </header>
      <dl className={styles.proposedChangeList}>
        {rows.map((change, index) => (
          <ProposedChangeRow key={`${index}-${change.label}`} change={change} isStale={hasWriteAccess && status === ProposedChangeStatus.STALE} />
        ))}
      </dl>
      {renderNotice()}
      {renderFooter()}
    </article>
  );
}
